import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { TokenService } from './token.service';
import { AuthenticateApiService } from './authenticate-api.service';
import { ToastService } from './toast-service';
import { PresenceService } from './presence.service';

const WARNING_THRESHOLD_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SessionService {
    private countdownSubject = new BehaviorSubject<string>('');
    countdown$: Observable<string> = this.countdownSubject.asObservable();

    private warningSubject = new BehaviorSubject<boolean>(false);
    warning$: Observable<boolean> = this.warningSubject.asObservable();

    private intervalId: any = null;
    private warningShown = false;
    private refreshInFlight$: Observable<any> | null = null;

    constructor(
        private ngZone: NgZone,
        private router: Router,
        private toastService: ToastService,
        private tokenService: TokenService,
        private authenticateApiService: AuthenticateApiService,
        private presenceService: PresenceService,
    ) { }

    start(): void {
        if (this.intervalId) return;
        this.tick();
        this.ngZone.runOutsideAngular(() => {
            this.intervalId = setInterval(() => this.ngZone.run(() => this.tick()), 1000);
        });
    }

    stop(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.warningShown = false;
        this.warningSubject.next(false);
        this.countdownSubject.next('');
    }

    // Refreshes the access token, sharing one in-flight request between the
    // warning UI's "Extend" click and any concurrent 401 retries.
    refresh(): Observable<any> {
        if (this.refreshInFlight$) return this.refreshInFlight$;

        const request$ = this.authenticateApiService.refresh().pipe(
            tap(response => {
                this.tokenService.initializeSession({
                    user: response.user,
                    permissionNames: response.permissionNames,
                    branch: this.tokenService.getBranch(),
                    roles: response.roles,
                    expiresOnUtc: response.expiresOnUtc,
                });
                this.warningShown = false;
                this.warningSubject.next(false);
            }),
            shareReplay(1),
            finalize(() => { this.refreshInFlight$ = null; }),
        );

        this.refreshInFlight$ = request$;
        return request$;
    }

    extend(): void {
        this.refresh().subscribe({
            next: () => this.toastService.showSuccessToast("Session extended. You'll stay signed in."),
            error: () => this.forceLogout(),
        });
    }

    private tick(): void {
        const expiryIso = this.tokenService.getExpiryTime();
        if (!expiryIso) { this.countdownSubject.next(''); return; }

        const expiryMs = this.parseIsoToMs(expiryIso);
        if (isNaN(expiryMs)) { this.countdownSubject.next('Invalid expiry'); return; }

        const diff = expiryMs - Date.now();
        if (diff <= 0) {
            this.countdownSubject.next('Session expired');
            this.stop();
            this.forceLogout();
            return;
        }

        const hrs = Math.floor(diff / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        this.countdownSubject.next(`${hrs}h ${mins}m ${secs}s`);

        if (diff <= WARNING_THRESHOLD_MS && !this.warningShown) {
            this.warningShown = true;
            this.warningSubject.next(true);
        }
    }

    private forceLogout(): void {
        this.presenceService.stop();
        this.tokenService.clear();
        this.warningSubject.next(false);
        this.router.navigateByUrl('/login');
    }

    private parseIsoToMs(iso: string): number {
        let s = iso?.trim();
        if (!s) return NaN;
        if (!/[zZ]|[+\-]\d{2}:\d{2}$/.test(s)) s = s + 'Z';
        return Date.parse(s);
    }
}
